import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../../../utils/Auth';
import { ColorAndGradientToggles, ColorPickers, Rotation, Transparency } from '../helper';
import { AnimatePresence, motion } from 'motion/react';
import { transition, variants } from '../variants';
import styles from './Background.module.css';

const getInitialColor = (options, isSecondColor = false) => {
    const colorStops = options?.gradient?.colorStops;
    if (isSecondColor) {
        return colorStops?.[1]?.color || '#ffffff';
    }
    return options?.color === 'TRANSPARENT'
        ? '#ffffff'
        : options?.color || colorStops?.[0]?.color || '#ffffff';
};

// Main Component
export default function Background() {
    const { qr_options, optionsDispatch } = useAuth();
    const backgroundOptions = qr_options?.backgroundOptions;

    const colorType = useMemo(() => backgroundOptions?.gradient ? 'gradient' : 'single', [backgroundOptions?.gradient]);
    const gradientType = useMemo(() => backgroundOptions?.gradient?.type || 'linear', [backgroundOptions?.gradient?.type]);
    const firstColor = useMemo(() => getInitialColor(backgroundOptions), [backgroundOptions]);
    const secondColor = useMemo(() => getInitialColor(backgroundOptions, true), [backgroundOptions]);
    const rotation = useMemo(() => backgroundOptions?.gradient?.rotation || '0', [backgroundOptions?.gradient?.rotation]);
    const transparent = useMemo(() => backgroundOptions?.color === 'TRANSPARENT', [backgroundOptions?.color]);
    
    const [displayColorPicker, setDisplayColorPicker] = useState(null);

    const handleColorTypeChange = (e) => {
        const newColorType = e.target.checked ? 'gradient' : 'single';

        if (newColorType === 'single') {
            optionsDispatch({ type: 'background', options: { color: firstColor, gradient: null }});
        } else {
            optionsDispatch({ type: 'background', options: { color: null, gradient: { colorStops: [{ offset: 0, color: firstColor }, { offset: 1, color: secondColor }], type: gradientType }}});
        }
    };

    const handleGradientTypeChange = (e) => {
        const newGradientType = e.target.checked ? 'radial' : 'linear';
        optionsDispatch({ type: 'background', options: { gradient: { ...backgroundOptions.gradient, type: newGradientType }}});
    };

    const handleColorChange = useCallback((color, name) => {
        if (colorType === 'single') {
            optionsDispatch({ type: 'background', options: { color: color }});
        } else {
            if (name === 'single-color') {
                optionsDispatch({ type: 'background', options: { gradient: { ...backgroundOptions.gradient, colorStops: [{ offset: 0, color: color }, { offset: 1, color: secondColor }]}}});
            } else if (name === 'gradient-color') {
                optionsDispatch({ type: 'background', options: { gradient: { ...backgroundOptions.gradient, colorStops: [{ offset: 0, color: firstColor }, { offset: 1, color: color }]}}});
            };
        };
    },[colorType, firstColor, secondColor, backgroundOptions, optionsDispatch]);

    const handleRotationChange = (e) => {
        const rotation = e.target.value;
        optionsDispatch({ type: 'background', options: { gradient: { ...backgroundOptions.gradient, rotation: rotation }}});
    };

    const handleTransparentChange = (checked) => {
        if (checked) {
            optionsDispatch({ type: 'background', options: { color: 'TRANSPARENT', gradient: null }});
        } else {
            if (colorType === 'single') {
                const restoredColor = backgroundOptions?.color !== 'TRANSPARENT' && backgroundOptions?.color ? backgroundOptions.color : '#ffffff';
                optionsDispatch({ type: 'background', options: { color: restoredColor, gradient: null }});
            } else {
                const restoredFirstColor = backgroundOptions?.gradient?.colorStops?.[0]?.color || '#ffffff';
                const restoredSecondColor = backgroundOptions?.gradient?.colorStops?.[1]?.color || '#ffffff';
                optionsDispatch({ type: 'background', options: { gradient: { ...backgroundOptions.gradient, colorStops: [{ offset: 0, color: restoredFirstColor }, { offset: 1, color: restoredSecondColor }]}}})
            }
        }
    };

    return (
        <div className={styles.container}>
            <Transparency transparent={transparent} handleTransparentChange={handleTransparentChange} />
            <AnimatePresence mode='wait'>
                {!transparent && (
                    <motion.div
                        className={styles.inputGroup}
                        style={{marginBottom: '0'}}
                        transition={transition}
                        variants={variants}
                        initial='initial'
                        animate='animate'
                        exit='initial'
                    >
                       <div className={styles.inputGroup} style={{flexDirection: 'row', gap: '0 1.25rem'}}>
                            <ColorAndGradientToggles
                                colorType={colorType}
                                handleColorTypeChange={handleColorTypeChange}
                                gradientType={gradientType}
                                handleGradientTypeChange={handleGradientTypeChange}
                            />
                            <Rotation
                                show={colorType === 'gradient' && gradientType === 'linear'}
                                rotation={rotation}
                                handleRotationChange={handleRotationChange}
                            />
                       </div>
                       <div className={styles.inputGroup} style={{marginBottom: '0'}}>
                            <div className={styles.label}>Background {colorType === 'single' ? 'Color' : 'Gradient'}</div>
                            <ColorPickers
                                colorType={colorType}
                                firstColor={firstColor}
                                secondColor={secondColor}
                                handleColorChange={handleColorChange}
                                displayColorPicker={displayColorPicker}
                                setDisplayColorPicker={setDisplayColorPicker}
                            />
                       </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};